import {
  makeGETRequestOnUserService,
  makePOSTRequestOnUserService,
  makePatchRequestOnUserService,
} from "./api";
import {
  getAllowedMFInstallmentDays,
  isValidMFInstallmentDay,
  mfFrequencyNeedsInstallmentDay,
  normalizeMFFrequency,
} from "../Helpers/mfSchedule";

export type MFRecord = Record<string, any>;

export interface MFScheme extends MFRecord {
  fund_scheme_id: number;
  name: string;
  isin: string;
  amc_id?: number;
  logo?: string | null;
  fund_category?: string | null;
  sub_category?: string | null;
  active?: boolean;
  purchase_allowed?: boolean;
  sip_allowed?: boolean;
  redemption_allowed?: boolean;
  switch_in_allowed?: boolean;
  switch_out_allowed?: boolean;
  min_initial_investment?: number | null;
  max_initial_investment?: number | null;
  initial_investment_multiples?: number | null;
  min_withdrawal_amount?: number | null;
  max_withdrawal_amount?: number | null;
  withdrawal_multiples?: number | null;
  min_switch_in_amount?: number | null;
  max_switch_in_amount?: number | null;
  switch_in_amount_multiples?: number | null;
  min_switch_out_amount?: number | null;
  max_switch_out_amount?: number | null;
  switch_out_amount_multiples?: number | null;
  lock_in?: boolean;
  lock_in_period?: number | string | null;
  sip_frequency_specific_data?: Record<string, MFSipFrequencyRule>;
}

export interface MFSipFrequencyRule {
  dates?: number[];
  min_installment_amount?: number | null;
  max_installment_amount?: number | null;
  amount_multiples?: number | null;
  min_installments?: number | null;
}

export interface MFPlanThreshold extends MFRecord {
  type?: string | null;
  frequency?: string | null;
  amount_min?: number | null;
  amount_max?: number | null;
  amount_multiples?: number | null;
  installments_min?: number | null;
  dates?: number[] | null;
}

export interface MFSchemeDetails extends MFRecord {
  data: MFScheme;
  scheme_plans_v2?: { thresholds?: MFPlanThreshold[] | null } | null;
  schemes_details?: MFRecord | null;
  schemes_detalis?: MFRecord | null;
}

export interface MFInvestmentRule {
  minimum: number;
  maximum: number;
  multiple: number;
  installmentsMinimum: number;
  dates: number[];
  frequency: string | null;
}

export interface MFAmcCompany extends MFRecord {
  company_name?: string | null;
  isin_list?: string[] | null;
}

export interface MFAdminDashboardData {
  reports: MFRecord | null;
  purchases: MFRecord[];
  sipPlans: MFRecord[];
  redemptions: MFRecord[];
  switches: MFRecord[];
  warnings: string[];
}

export interface MFTransactionSource {
  idField: "purchase_id" | "purchase_plan_id";
  id: string;
  folioNumber: string;
  redeemableAmount: number | null;
}

export type MFTransactionKind = "redemption" | "switch";

export interface MFTransactionSubmission {
  id: string;
  confirmed: boolean;
  warning?: string;
}

export interface MFSipSkipDateRange {
  installmentDate: string;
  fromDate: string;
  toDate: string;
}

const responseData = (response: any): any => response?.data ?? response;

const listFrom = (payload: any): MFRecord[] => {
  const value = responseData(payload);
  const findList = (candidate: any): MFRecord[] => {
    if (Array.isArray(candidate)) return candidate;
    if (!candidate || typeof candidate !== "object") return [];
    for (const key of ["data", "results", "purchases", "purchase_plans"]) {
      const nested = candidate[key];
      const result = findList(nested);
      if (result.length || Array.isArray(nested)) return result;
    }
    for (const nested of Object.values(candidate)) {
      const result = findList(nested);
      if (result.length) return result;
    }
    return [];
  };
  const result = findList(value);
  if (result.length) return result;
  return [];
};

const assertEnvelope = (response: any, fallback: string): any => {
  const payload = responseData(response);
  if (
    payload?.success === false ||
    String(payload?.status || "").trim().toLowerCase() === "failed"
  ) {
    throw new Error(payload?.error || payload?.message || fallback);
  }
  return payload;
};

const buildAdminPostbackUrl = (returnUrl: string, values: Record<string, string>) => {
  const dashboard = new URL(returnUrl);
  Array.from(dashboard.searchParams.keys())
    .filter((key) => key.startsWith("mf_admin_"))
    .forEach((key) => dashboard.searchParams.delete(key));
  const callback = new URL("/mf-payment-return", dashboard.origin);
  callback.searchParams.set(
    "mf_admin_return",
    `${dashboard.pathname}${dashboard.search}`
  );
  Object.entries(values).forEach(([key, value]) => callback.searchParams.set(key, value));
  return callback.toString();
};

export class MFMandatePendingError extends Error {
  readonly code = "MF_MANDATE_PENDING";

  constructor(message = "The bank has received the mandate, but approval is still pending.") {
    super(message);
    this.name = "MFMandatePendingError";
  }
}

export const isMFMandatePendingError = (error: unknown): error is MFMandatePendingError =>
  error instanceof MFMandatePendingError || (error as any)?.code === "MF_MANDATE_PENDING";

const findString = (payload: any, keys: string[]): string => {
  if (!payload || typeof payload !== "object") return "";
  for (const key of keys) {
    if (typeof payload[key] === "string" && payload[key].trim()) {
      return payload[key].trim();
    }
    if (typeof payload[key] === "number" && Number.isFinite(payload[key])) {
      return String(payload[key]);
    }
  }
  for (const value of Object.values(payload)) {
    if (value && typeof value === "object") {
      const match = findString(value, keys);
      if (match) return match;
    }
  }
  return "";
};

const findValue = (payload: any, keys: string[]): any => {
  if (!payload || typeof payload !== "object") return undefined;
  for (const key of keys) {
    if (payload[key] !== undefined && payload[key] !== null) return payload[key];
  }
  for (const value of Object.values(payload)) {
    if (value && typeof value === "object") {
      const match = findValue(value, keys);
      if (match !== undefined) return match;
    }
  }
  return undefined;
};

const isPositiveMultiple = (amount: number, multiple: number) => {
  if (!Number.isFinite(amount) || !Number.isFinite(multiple) || multiple <= 0) return false;
  const quotient = amount / multiple;
  return Math.abs(quotient - Math.round(quotient)) < 1e-8;
};

const operationAllowed = (
  details: MFSchemeDetails,
  operation: "purchase" | "sip" | "redemption" | "switch_in" | "switch_out"
) => details.data?.[`${operation}_allowed`] === true;

const getInvestmentRule = (
  details: MFSchemeDetails,
  type: "lumpsum" | "sip",
  frequency?: string
): MFInvestmentRule => {
  const normalizedFrequency = frequency?.trim().toLowerCase();
  const threshold = (details.scheme_plans_v2?.thresholds || []).find((candidate) => {
    if (String(candidate.type || "").trim().toLowerCase() !== type) return false;
    return type === "lumpsum" ||
      String(candidate.frequency || "").trim().toLowerCase() === normalizedFrequency;
  });
  const minimum = Number(threshold?.amount_min);
  const maximum = Number(threshold?.amount_max);
  const multiple = Number(threshold?.amount_multiples);
  if (
    !threshold ||
    !Number.isFinite(minimum) ||
    !Number.isFinite(maximum) ||
    maximum < minimum ||
    !Number.isFinite(multiple) ||
    multiple <= 0
  ) {
    throw new Error(`The authoritative ${type} limits are unavailable for this scheme.`);
  }
  return {
    minimum,
    maximum,
    multiple,
    installmentsMinimum: Math.max(1, Math.ceil(Number(threshold.installments_min || 1))),
    // Intersected with what the frequency actually permits: a weekly SIP takes
    // a weekday 1-5, a monthly one a day of the month 1-28. See Helpers/mfSchedule.
    dates: getAllowedMFInstallmentDays(
      normalizedFrequency || "",
      (threshold.dates || []).filter((date) => Number.isInteger(date))
    ),
    frequency: normalizedFrequency || null,
  };
};

export const getAdminInvestmentRule = getInvestmentRule;

const validateLumpsumInput = (details: MFSchemeDetails, amount: number) => {
  const scheme = details.data;
  const rule = getInvestmentRule(details, "lumpsum");
  if (
    scheme.active === false ||
    scheme.merged === true ||
    !operationAllowed(details, "purchase") ||
    !Number.isFinite(amount) ||
    amount <= 0 ||
    amount < rule.minimum ||
    amount > rule.maximum ||
    !isPositiveMultiple(amount, rule.multiple) ||
    !isPositiveMultiple(amount, 10)
  ) {
    throw new Error("This amount is not eligible for a lumpsum order in the selected scheme.");
  }
};

const validateSipInput = (input: {
  schemeDetails: MFSchemeDetails;
  amount: number;
  frequency: string;
  installmentDay?: number;
  installments: number;
}) => {
  const scheme = input.schemeDetails.data;
  const rule = getInvestmentRule(input.schemeDetails, "sip", input.frequency);
  // A frequency either takes an installment day or it does not, and when it
  // does the day has to be one the frequency AND the scheme both permit —
  // a weekday for weekly/fortnightly, a day of the month otherwise.
  const needsDay = mfFrequencyNeedsInstallmentDay(input.frequency);
  const scheduleIsValid = needsDay
    ? isValidMFInstallmentDay(
        input.frequency,
        input.installmentDay as number,
        rule.dates
      )
    : input.installmentDay == null;
  if (
    scheme.active === false ||
    scheme.merged === true ||
    !operationAllowed(input.schemeDetails, "sip") ||
    !Number.isFinite(input.amount) ||
    input.amount <= 0 ||
    input.amount < rule.minimum ||
    input.amount > rule.maximum ||
    !isPositiveMultiple(input.amount, rule.multiple) ||
    !isPositiveMultiple(input.amount, 10) ||
    !Number.isInteger(input.installments) ||
    input.installments < rule.installmentsMinimum ||
    !scheduleIsValid
  ) {
    throw new Error("These SIP amount or schedule details are no longer eligible for this scheme.");
  }
};

export const getMFErrorMessage = (error: any, fallback = "The request could not be completed.") =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const formatUtcDate = (date: Date) =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(
    date.getUTCDate()
  ).padStart(2, "0")}`;

export function getAdminSipSkipDateRange(order: MFRecord, now = new Date()): MFSipSkipDateRange {
  if (order.systematic !== true || !order.purchase_plan_id) {
    throw new Error("Only an active SIP plan can be skipped.");
  }
  if (String(order.state || "").trim().toLowerCase() !== "active") {
    throw new Error("This SIP is not active.");
  }
  if (order.cancellation_scheduled_on) {
    throw new Error("This SIP already has a cancellation scheduled.");
  }
  if (order.auto_generate_installments !== true) {
    throw new Error("Automatic installment generation is not enabled for this SIP.");
  }
  if (Number(order.remaining_installments) === 0) {
    throw new Error("This SIP has no remaining installments.");
  }
  // Skipping works off a day of the month, so only the month-day frequencies
  // support it — the same four the user platform allows. Normalised first so a
  // plan stored as "annual" is not rejected for being spelled differently.
  const frequency = normalizeMFFrequency(
    String(order.frequency || "").replace(/[- ]/g, "_")
  );
  if (!["monthly", "quarterly", "half_yearly", "yearly"].includes(frequency)) {
    throw new Error("This SIP frequency does not support installment skipping.");
  }
  const installmentDay = Number(order.installment_day);
  if (!Number.isInteger(installmentDay) || installmentDay < 1 || installmentDay > 31) {
    throw new Error("The SIP installment day is unavailable.");
  }
  const indiaTodayParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const todayPart = (type: Intl.DateTimeFormatPartTypes) =>
    Number(indiaTodayParts.find((part) => part.type === type)?.value);
  const todayYear = todayPart("year");
  const todayMonth = todayPart("month");
  const todayDay = todayPart("day");
  let installmentMonthIndex = todayMonth - 1 + (installmentDay <= todayDay ? 1 : 0);
  const getInstallment = (monthIndex: number) => {
    const date = new Date(Date.UTC(todayYear, monthIndex, installmentDay));
    return date.getUTCDate() === installmentDay ? date : null;
  };
  let installment = getInstallment(installmentMonthIndex);
  if (!installment) throw new Error("The next installment date is unavailable.");
  const startTimestamp = Date.parse(`${String(order.start_date || "").slice(0, 10)}T00:00:00.000Z`);
  while (Number.isFinite(startTimestamp) && installment.getTime() < startTimestamp) {
    installmentMonthIndex += 1;
    installment = getInstallment(installmentMonthIndex);
    if (!installment) throw new Error("The next installment date is unavailable.");
  }
  const endTimestamp = Date.parse(`${String(order.end_date || "").slice(0, 10)}T23:59:59.999Z`);
  if (Number.isFinite(endTimestamp) && installment.getTime() > endTimestamp) {
    throw new Error("This SIP has no future installment available to skip.");
  }
  const following = getInstallment(installmentMonthIndex + 1);
  if (!following) throw new Error("The SIP skip window could not be calculated.");
  const from = new Date(installment.getTime() - 86_400_000);
  const to = new Date(following.getTime() - 86_400_000);
  const todayTimestamp = Date.UTC(todayYear, todayMonth - 1, todayDay);
  if (Math.floor((from.getTime() - todayTimestamp) / 86_400_000) < 2) {
    throw new Error("The two-day cutoff for skipping this installment has passed.");
  }

  return {
    installmentDate: formatUtcDate(installment),
    fromDate: formatUtcDate(from),
    toDate: formatUtcDate(to),
  };
}

export async function fetchMFAdminDashboard(
  userUuid: string
): Promise<MFAdminDashboardData> {
  const requests: Array<{ key: string; label: string; promise: Promise<any> }> = [
    {
      key: "reports",
      label: "Portfolio",
      promise: makeGETRequestOnUserService(
        `/mutual_fund/reports_by_user_uuid/?user_uuid=${encodeURIComponent(userUuid)}`
      ) as Promise<any>,
    },
    {
      key: "purchases",
      label: "Lumpsum orders",
      promise: makeGETRequestOnUserService(
        `/mutual_fund/mf_purchase/get_by_user/${encodeURIComponent(userUuid)}/`
      ) as Promise<any>,
    },
    {
      key: "sipPlans",
      label: "SIP orders",
      promise: makeGETRequestOnUserService(
        `/mutual_fund/mf_purchase_plans/list/?user_id=${encodeURIComponent(userUuid)}`
      ) as Promise<any>,
    },
    {
      key: "redemptions",
      label: "Redemptions",
      promise: makeGETRequestOnUserService(
        `/mutual_fund/mf_redemptions/list/?user_id=${encodeURIComponent(userUuid)}`
      ) as Promise<any>,
    },
    {
      key: "switches",
      label: "Switches",
      promise: makeGETRequestOnUserService(
        `/mutual_fund/mf_switches/list/?user_id=${encodeURIComponent(userUuid)}`
      ) as Promise<any>,
    },
  ];

  const settled = await Promise.allSettled(requests.map((request) => request.promise));
  const result: MFAdminDashboardData = {
    reports: null,
    purchases: [],
    sipPlans: [],
    redemptions: [],
    switches: [],
    warnings: [],
  };

  settled.forEach((entry, index) => {
    const request = requests[index];
    if (entry.status === "rejected") {
      result.warnings.push(`${request.label}: ${getMFErrorMessage(entry.reason)}`);
      return;
    }
    try {
      const payload = assertEnvelope(entry.value, `${request.label} could not be loaded.`);
      if (request.key === "reports") {
        result.reports = payload?.data?.investor_reports ? payload.data : payload;
        return;
      }
      (result as any)[request.key] = listFrom(payload);
    } catch (error) {
      result.warnings.push(`${request.label}: ${getMFErrorMessage(error)}`);
    }
  });

  return result;
}

export async function fetchMFSchemes(): Promise<MFScheme[]> {
  const response = assertEnvelope(
    await makeGETRequestOnUserService("/mutual_fund/get_schemes/"),
    "The mutual fund scheme list could not be loaded."
  );
  return listFrom(response) as MFScheme[];
}

export async function fetchMFSchemesByFundName(fundName: string): Promise<MFScheme[]> {
  const normalizedFundName = fundName.trim();
  if (!normalizedFundName) return [];
  const response = assertEnvelope(
    await makeGETRequestOnUserService(
      `/mutual_fund/get_schemes/?fund_name=${encodeURIComponent(normalizedFundName)}`
    ),
    "The AMC scheme list could not be loaded."
  );
  return listFrom(response) as MFScheme[];
}

export async function fetchMFSchemeDetails(isin: string): Promise<MFSchemeDetails> {
  const response = assertEnvelope(
    await makeGETRequestOnUserService(
      `/mutual_fund/get_schemes_by_isin/?isin=${encodeURIComponent(isin)}`
    ),
    "The authoritative scheme details could not be loaded."
  ) as MFSchemeDetails;
  if (!response?.data || String(response.data.isin || "").toUpperCase() !== isin.toUpperCase()) {
    throw new Error("The scheme details response did not match the selected scheme.");
  }
  return response;
}

export async function fetchMFCompanies(): Promise<MFAmcCompany[]> {
  const response = assertEnvelope(
    await makeGETRequestOnUserService("/mutual_fund/all_funds_companies/"),
    "The AMC directory could not be loaded."
  );
  return listFrom(response) as MFAmcCompany[];
}

export async function placeAdminLumpsum(input: {
  userUuid: string;
  scheme: MFScheme;
  amount: number;
  returnUrl: string;
}) {
  const authoritativeDetails = await fetchMFSchemeDetails(input.scheme.isin);
  validateLumpsumInput(authoritativeDetails, input.amount);
  const authoritativeScheme = authoritativeDetails.data;
  const created = assertEnvelope(
    await makePOSTRequestOnUserService("/mutual_fund/mf_purchase/create/", {
      user_uuid: input.userUuid,
      isin: input.scheme.isin,
      amount: input.amount,
      lock_in: Boolean(authoritativeScheme.lock_in),
      lock_in_period: authoritativeScheme.lock_in_period ?? null,
    }),
    "The lumpsum order could not be created."
  );
  const purchaseId = findString(created, ["purchase_id", "id"]);
  if (!purchaseId) throw new Error("The purchase ID was not returned.");

  assertEnvelope(
    await makePatchRequestOnUserService("/mutual_fund/mf_purchase/update/", {
      purchase_id: purchaseId,
    }),
    "The purchase could not be confirmed."
  );

  const callbackUrl = buildAdminPostbackUrl(input.returnUrl, {
    mf_admin_payment: purchaseId,
  });
  const payment = assertEnvelope(
    await makePOSTRequestOnUserService("/mutual_fund/mf_payments/create/", {
      purchase_id: purchaseId,
      payment_postback_url: callbackUrl,
    }),
    "The payment session could not be created."
  );
  return {
    purchaseId,
    redirectUrl: findString(payment, [
      "token_url",
      "redirect_url",
      "payment_url",
      "payment_redirect_url",
      "url",
    ]),
  };
}

export async function completeAdminLumpsumPayment(purchaseId: string) {
  return assertEnvelope(
    await makePatchRequestOnUserService(
      `/mutual_fund/mf_purchase/update_db/${encodeURIComponent(purchaseId)}/`,
      {}
    ),
    "The completed payment could not be reconciled."
  );
}

export async function startAdminSip(input: {
  userUuid: string;
  scheme: MFScheme;
  amount: number;
  frequency: string;
  installmentDay?: number;
  installments: number;
  firstInstallmentNow: boolean;
  returnUrl: string;
}) {
  const authoritativeDetails = await fetchMFSchemeDetails(input.scheme.isin);
  validateSipInput({ ...input, schemeDetails: authoritativeDetails });
  const mandate = assertEnvelope(
    await makePOSTRequestOnUserService("/mutual_fund/mf_mandates/create/", {
      user_uuid: input.userUuid,
      mandate_type: "E_MANDATE",
      mandate_limit: input.amount,
    }),
    "The e-mandate could not be created."
  );
  const mandateId = findString(mandate, ["mandate_id", "id"]);
  if (!mandateId) throw new Error("The mandate ID was not returned.");

  const callbackUrl = buildAdminPostbackUrl(input.returnUrl, {
    mf_admin_sip_mandate: mandateId,
    mf_admin_sip_isin: input.scheme.isin,
    mf_admin_sip_amount: String(input.amount),
    mf_admin_sip_frequency: input.frequency,
    mf_admin_sip_installments: String(input.installments),
    mf_admin_sip_first: input.firstInstallmentNow ? "true" : "false",
    ...(input.installmentDay ? { mf_admin_sip_day: String(input.installmentDay) } : {}),
  });

  const auth = assertEnvelope(
    await makePOSTRequestOnUserService("/mutual_fund/mf_mandates/emandate/auth/", {
      mandate_id: mandateId,
      payment_postback_url: callbackUrl,
    }),
    "The mandate authorization session could not be created."
  );
  return {
    mandateId,
    redirectUrl: findString(auth, ["token_url", "redirect_url", "payment_url", "url"]),
  };
}

export async function completeAdminSip(input: {
  userUuid: string;
  mandateId: string;
  scheme: MFScheme;
  amount: number;
  frequency: string;
  installmentDay?: number;
  installments: number;
  firstInstallmentNow: boolean;
}) {
  const authoritativeDetails = await fetchMFSchemeDetails(input.scheme.isin);
  validateSipInput({ ...input, schemeDetails: authoritativeDetails });
  const authoritativeScheme = authoritativeDetails.data;
  const mandate = assertEnvelope(
    await makeGETRequestOnUserService(
      `/mutual_fund/mf_mandates/list/sync/?mandate_id=${encodeURIComponent(input.mandateId)}`
    ),
    "The mandate status could not be verified."
  );
  const returnedMandateId = findString(mandate, ["id", "mandate_id"]);
  if (!returnedMandateId || returnedMandateId !== String(input.mandateId)) {
    throw new Error("The verified mandate does not match this SIP authorization.");
  }
  const mandateLimit = Number(findValue(mandate, ["mandate_limit"]));
  if (!Number.isFinite(mandateLimit) || input.amount > mandateLimit) {
    throw new Error("The SIP installment exceeds the approved mandate debit limit.");
  }
  const status = String(findValue(mandate, ["mandate_status", "status", "state"]) || "")
    .trim()
    .toUpperCase();
  if (["CREATED", "RECEIVED", "SUBMITTED", "PENDING"].includes(status)) {
    throw new MFMandatePendingError();
  }
  if (status !== "APPROVED") {
    throw new Error(status ? `The mandate is ${status.toLowerCase()}.` : "The mandate status is unavailable.");
  }

  const created = assertEnvelope(
    await makePOSTRequestOnUserService("/mutual_fund/mf_purchase_plans/create/", {
      user_uuid: input.userUuid,
      isin: input.scheme.isin,
      amount: input.amount,
      frequency: input.frequency,
      ...(mfFrequencyNeedsInstallmentDay(input.frequency) && input.installmentDay
        ? { installment_day: String(input.installmentDay) }
        : {}),
      systematic: true,
      number_of_installments: input.installments,
      payment_method: "mandate",
      payment_source: input.mandateId,
      auto_generate_installments: true,
      generate_first_installment_now: input.firstInstallmentNow,
      lock_in: Boolean(authoritativeScheme.lock_in),
      lock_in_period: authoritativeScheme.lock_in_period ?? null,
    }),
    "The SIP plan could not be created."
  );
  const purchasePlanId = findString(created, ["purchase_plan_id", "id"]);
  if (!purchasePlanId) throw new Error("The SIP purchase-plan ID was not returned.");
  const createdState = findString(created, ["state"]);
  if (createdState.toLowerCase() !== "created") {
    throw new Error(`The SIP plan was returned in an unexpected ${createdState} state.`);
  }

  const updated = assertEnvelope(
    await makePatchRequestOnUserService("/mutual_fund/mf_purchase_plans/update/", {
      purchase_plan_id: purchasePlanId,
      state: "confirmed",
    }),
    "The SIP plan could not be confirmed."
  );
  const confirmedPlanId = findString(updated, ["purchase_plan_id", "id"]);
  if (confirmedPlanId !== purchasePlanId) {
    throw new Error("The confirmed SIP does not match the plan that was created.");
  }
  const confirmedState = findString(updated, ["state"]);
  if (confirmedState.toLowerCase() !== "confirmed") {
    throw new Error("The SIP was created but has not been confirmed yet.");
  }

  let syncWarning = "";
  try {
    let lastSyncError: any;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const details = assertEnvelope(
          await makeGETRequestOnUserService(
            `/mutual_fund/mf_purchase_plans/get/${encodeURIComponent(purchasePlanId)}/`
          ),
          "The confirmed SIP details could not be refreshed."
        );
        const fetchedPlanId = findString(details, ["purchase_plan_id", "id"]);
        if (fetchedPlanId !== purchasePlanId) {
          throw new Error("The refreshed SIP does not match the confirmed plan.");
        }
        const investmentAccount =
          findString(details, ["mf_investment_account"]) ||
          findString(updated, ["mf_investment_account"]);
        if (!investmentAccount) {
          throw new Error("The SIP investment account was not returned for status synchronization.");
        }
        assertEnvelope(
          await makeGETRequestOnUserService(
            `/mutual_fund/mf_purchase_plans/list/sync/?mf_investment_account=${encodeURIComponent(
              investmentAccount
            )}`
          ),
          "The confirmed SIP status could not be synchronized."
        );
        lastSyncError = undefined;
        break;
      } catch (error) {
        lastSyncError = error;
      }
    }
    if (lastSyncError) throw lastSyncError;
  } catch (error) {
    // The purchase plan is already confirmed. Do not make the callback retry
    // plan creation and risk a duplicate merely because status sync failed.
    syncWarning = getMFErrorMessage(error);
  }
  return { purchasePlanId, syncWarning };
}

export async function cancelAdminLumpsum(purchaseId: string) {
  const normalizedPurchaseId = purchaseId.trim();
  if (!normalizedPurchaseId) throw new Error("The purchase ID is missing for this order.");
  const response = assertEnvelope(
    await makePOSTRequestOnUserService(
      `/mutual_fund/mf_purchase/cancel/${encodeURIComponent(normalizedPurchaseId)}/`,
      {}
    ),
    "The lumpsum order could not be cancelled."
  );
  const returnedPurchaseId = findString(response, ["purchase_id", "id"]);
  if (returnedPurchaseId && returnedPurchaseId !== normalizedPurchaseId) {
    throw new Error("The cancellation response did not match this purchase.");
  }
  return response;
}

async function cancelAdminMandate(mandateId: string) {
  const normalizedMandateId = mandateId.trim();
  if (!normalizedMandateId) throw new Error("The linked mandate ID is missing.");
  const response = assertEnvelope(
    await makePOSTRequestOnUserService(
      `/mutual_fund/mf_mandates/cancel/${encodeURIComponent(normalizedMandateId)}/`,
      {}
    ),
    "The linked mandate could not be cancelled."
  );
  const returnedMandateId = findString(response, ["id", "mandate_id"]);
  if (returnedMandateId && returnedMandateId !== normalizedMandateId) {
    throw new Error("The mandate cancellation response did not match the linked mandate.");
  }
  const status = String(findValue(response, ["mandate_status"]) || "").trim().toUpperCase();
  if (status !== "CANCELLED") {
    throw new Error(status
      ? `The SIP was cancelled, but the linked mandate is still ${status.toLowerCase()}.`
      : "The SIP was cancelled, but the linked mandate status could not be confirmed.");
  }
  return response;
}

export async function cancelAdminSip(input: {
  purchasePlanId: string;
  mandateId?: string;
  cancellationCode: string;
  cancellationReason?: string;
}) {
  const payload: MFRecord = {
    purchase_plan_id: input.purchasePlanId,
    cancellation_code: input.cancellationCode,
  };
  if (input.cancellationCode === "custom_reason") {
    const reason = input.cancellationReason?.trim();
    if (!reason) throw new Error("Enter a reason for cancelling this SIP.");
    payload.cancellation_reason = reason;
  }
  const result = assertEnvelope(
    await makePOSTRequestOnUserService("/mutual_fund/mf_purchase_plans/cancel/", payload),
    "The SIP could not be cancelled."
  );
  const cancelledPlanId = findString(result, ["purchase_plan_id", "id"]);
  if (cancelledPlanId && cancelledPlanId !== input.purchasePlanId) {
    throw new Error("The cancellation response did not match this SIP.");
  }
  let mandateCancellationWarning = "";
  if (input.mandateId) {
    try {
      await cancelAdminMandate(input.mandateId);
    } catch (error) {
      mandateCancellationWarning = getMFErrorMessage(error);
    }
  }
  let syncWarning = "";
  try {
    const details = assertEnvelope(
      await makeGETRequestOnUserService(
        `/mutual_fund/mf_purchase_plans/get/${encodeURIComponent(input.purchasePlanId)}/`
      ),
      "The cancelled SIP details could not be refreshed."
    );
    const returnedPlanId = findString(details, ["purchase_plan_id", "id"]);
    if (returnedPlanId !== input.purchasePlanId) {
      throw new Error("The refreshed SIP did not match the cancelled plan.");
    }
    const investmentAccount = findString(details, ["mf_investment_account"]);
    if (!investmentAccount) throw new Error("The cancelled SIP investment account was missing.");
    assertEnvelope(
      await makeGETRequestOnUserService(
        `/mutual_fund/mf_purchase_plans/list/sync/?mf_investment_account=${encodeURIComponent(
          investmentAccount
        )}`
      ),
      "The cancelled SIP status could not be synchronized."
    );
  } catch (error) {
    syncWarning = getMFErrorMessage(error);
  }
  return { result, mandateCancellationWarning, syncWarning };
}

export async function fetchSipSkipInstructions(purchasePlanId: string): Promise<MFRecord[]> {
  const response = assertEnvelope(
    await makeGETRequestOnUserService(
      `/mutual_fund/mf_purchase_plans/skip_instructions/list/${encodeURIComponent(
        purchasePlanId
      )}/`
    ),
    "The SIP skip instructions could not be loaded."
  );
  return listFrom(response).filter((instruction) => instruction.plan === purchasePlanId);
}

export async function skipAdminSip(purchasePlanId: string, fromDate: string, toDate: string) {
  const response = assertEnvelope(
    await makePOSTRequestOnUserService(
      `/mutual_fund/mf_purchase_plans/skip_instructions/create/${encodeURIComponent(
        purchasePlanId
      )}/`,
      { from_date: fromDate, to_date: toDate }
    ),
    "The SIP installment could not be skipped."
  );
  const instructionId = findString(response, ["id"]);
  const returnedPlanId = findString(response, ["plan"]);
  if (!instructionId || returnedPlanId !== purchasePlanId) {
    throw new Error("The created skip instruction did not match this SIP.");
  }
  return response;
}

export async function resumeAdminSip(skipInstructionId: string) {
  const response = assertEnvelope(
    await makePOSTRequestOnUserService(
      `/mutual_fund/mf_purchase_plans/skip_instructions/cancel/${encodeURIComponent(
        skipInstructionId
      )}/`,
      {}
    ),
    "The SIP skip instruction could not be cancelled."
  );
  if (findString(response, ["id"]) !== skipInstructionId) {
    throw new Error("The cancelled skip instruction did not match this request.");
  }
  return response;
}

export async function createAdminRedemption(amount: number, source: MFTransactionSource) {
  const created = assertEnvelope(
    await makePOSTRequestOnUserService("/mutual_fund/mf_redemptions/create/", {
      amount,
      [source.idField]: source.id,
    }),
    "The redemption could not be created."
  );
  const redemptionId = findString(created, ["redemption_id"]);
  if (!redemptionId) throw new Error("The redemption ID was not returned.");
  try {
    await confirmAdminTransaction("redemption", redemptionId);
    return { id: redemptionId, confirmed: true } as MFTransactionSubmission;
  } catch (error) {
    return {
      id: redemptionId,
      confirmed: false,
      warning: getMFErrorMessage(
        error,
        "The redemption was created but could not be confirmed."
      ),
    } as MFTransactionSubmission;
  }
}

export async function createAdminSwitch(
  amount: number,
  destinationIsin: string,
  source: MFTransactionSource
) {
  const created = assertEnvelope(
    await makePOSTRequestOnUserService("/mutual_fund/mf_switches/create/", {
      amount,
      isin_destination: destinationIsin,
      [source.idField]: source.id,
    }),
    "The switch could not be created."
  );
  const switchId = findString(created, ["switch_id"]);
  if (!switchId) throw new Error("The switch ID was not returned.");
  try {
    await confirmAdminTransaction("switch", switchId);
    return { id: switchId, confirmed: true } as MFTransactionSubmission;
  } catch (error) {
    return {
      id: switchId,
      confirmed: false,
      warning: getMFErrorMessage(
        error,
        "The switch was created but could not be confirmed."
      ),
    } as MFTransactionSubmission;
  }
}

export async function confirmAdminTransaction(kind: MFTransactionKind, id: string) {
  const isRedemption = kind === "redemption";
  const response = assertEnvelope(
    await makePatchRequestOnUserService(
      isRedemption
        ? "/mutual_fund/mf_redemptions/update/"
        : "/mutual_fund/mf_switches/update/",
      isRedemption ? { redemption_id: id } : { switch_id: id }
    ),
    `The ${kind} could not be confirmed.`
  );
  const returnedId = findString(response, [isRedemption ? "redemption_id" : "switch_id"]);
  if (returnedId !== id) {
    throw new Error(`The confirmed ${kind} does not match the created request.`);
  }
  return response;
}
